import React, { Component } from "react";
import { alert, loading, t } from "../../components/CustomComponent.js";
import { withRouter } from "../../components/withRouter.js";
import { typeMeta } from "../../components/asset_option.js";

import HomeNavbar from "./components/HomeNavbar.js";
import HomeFooter from "./components/HomeFooter.js";
import PropertyGallery from "./components/PropertyGallery.js";
import PropertyHead from "./components/PropertyHead.js";
import PropertyBody from "./components/PropertyBody.js";
import PropertySide from "./components/PropertySide.js";
import PropertySimilar from "./components/PropertySimilar.js";
import PropertyLead from "./components/PropertyLead.js";

import ICON from "./icons.js";
import { EMPTY_CONTENT, loadProperty, loadHomeContent, trackPropertyView, submitPropertyLead } from "./api.js";
import { favMap, favToggle, favSubscribe } from "./fav_store.js";
import { money, isRent, isSale, postedWord } from "./property_util.js";
import { openFreePost } from "./options.js";
import "../../assets/css/bootstrap.css";
import "../../assets/css/style.css";
import "../../assets/css/homepage.css";
import "../../assets/css/property_detail.css";

/* ============================================================================
 * PropertyDetail — หน้ารายละเอียดทรัพย์ฝั่งผู้ใช้ทั่วไป (/property/:id)
 *
 * โครงหน้า
 *   1. เมนูบน + เส้นทางนำทาง (breadcrumb)
 *   2. แกลเลอรีรูป                       PropertyGallery
 *   3. คอลัมน์ซ้าย  : หัวข้อ + ราคา       PropertyHead
 *                     เนื้อหาทั้งหมด      PropertyBody
 *      คอลัมน์ขวา  : ผู้ติดต่อ + ข้อมูล   PropertySide  (ติดหน้าจอตามการเลื่อน)
 *   4. ทรัพย์ใกล้เคียง                    PropertySimilar
 *   5. ฟุตเตอร์ + แถบปุ่มติดต่อสำหรับจอเล็ก
 *
 * ข้อมูลมาจาก GET home/properties/:uid ซึ่งส่งรายละเอียดทรัพย์ + ผู้ดูแลประกาศ
 * + ทรัพย์ใกล้เคียง มาในก้อนเดียว (backend ตัดข้อมูลภายในออกตั้งแต่ชั้น response
 * แล้ว หน้าเว็บจึงไม่มีทางเผลอแสดงเบอร์เจ้าของหรือโน้ตภายใน)
 * ========================================================================== */
class PropertyDetail extends Component {
  state = {
    data: null,
    similar: [],
    // ใช้แค่ส่วนฟุตเตอร์ (สโลแกน/ข่าวสาร/โซเชียล) ให้ตรงกับหน้าแรก
    content: EMPTY_CONTENT,
    favorites: favMap(), // { [uid]: true } อ่านจาก fav_store — คงอยู่ข้ามการรีเฟรช
    fav_pop: "",
    show_bar: false, // แถบปุ่มติดต่อล่างจอ : โผล่เมื่อเลื่อนพ้นแกลเลอรีแล้ว
    modal_lead: false, // กล่อง "ลงทะเบียนสนใจทรัพย์"
  };

  componentDidMount() {
    this.Get();
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.unsubscribeFav = favSubscribe(() => this.setState({ favorites: favMap() }));
  }

  componentDidUpdate(prevProps) {
    // กดการ์ด "ทรัพย์ใกล้เคียง" = เปลี่ยนเฉพาะ :id หน้าเดิม ต้องโหลดข้อมูลใหม่เอง
    if (prevProps.router.params.id !== this.props.router.params.id) this.Get();
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.onScroll);
    this.unsubscribeFav();
    clearTimeout(this.fav_timer);
  }

  onScroll = () => {
    const show_bar = window.scrollY > 520;
    if (show_bar !== this.state.show_bar) this.setState({ show_bar });
  };

  Get = async () => {
    loading(true);
    const { id } = this.props.router.params;
    const [data, content] = await Promise.all([loadProperty(id), loadHomeContent()]);
    loading(false);

    // ไม่พบประกาศ (ถูกปิด/ลบไปแล้ว หรือ url ผิด) — พากลับหน้าแรก
    if (!data) {
      await alert("", "warning", t("warning"), t("detail_not_found"), t("confirm"));
      window.location.href = "/";
      return;
    }

    document.title = `${data.title || data.project} | SafeSpace Life Living`;
    window.scrollTo({ top: 0 });
    this.setState({ data, similar: data.similar || [], content });

    // นับยอดเข้าชมหลังรู้แล้วว่าประกาศเปิดดูได้จริง — ยิงทิ้งไว้ ไม่ให้บล็อกการแสดงผล
    trackPropertyView(this.props.router.params.id);
  };

  /* เมนู/ปุ่มที่ยังไม่มีหน้าปลายทาง — แจ้งแบบเดียวกับหน้าแรก */
  soon = (name) => alert("", "info", t("coming_soon_title"), `${name} — ${t("coming_soon_desc")}`, t("confirm"));

  /* เขียนลง fav_store อย่างเดียว — state.favorites อัปเดตผ่าน favSubscribe */
  toggleFavorite = (uid) => {
    clearTimeout(this.fav_timer);
    favToggle(uid);
    this.setState({ fav_pop: uid });
    this.fav_timer = setTimeout(() => this.setState({ fav_pop: "" }), 600);
  };

  /* แชร์ประกาศ : คัดลอกลิงก์หน้านี้ไปวางที่ไหนก็ได้
     ไม่ใช้ copyText() ของ utils เพราะมันเด้ง Swal ภาษาอังกฤษของตัวเองมาซ้อน
     กับข้อความของหน้านี้ — หน้าเว็บฝั่งลูกค้าต้องเป็นภาษาตามที่ผู้ใช้เลือก */
  share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert("", "success", t("detail_share_copied"), t("detail_share_copied_desc"), t("confirm"));
    } catch (error) {
      // เบราว์เซอร์ไม่อนุญาตให้เขียนคลิปบอร์ด (เช่นเปิดผ่าน http) — โชว์ลิงก์ให้คัดลอกเอง
      alert("", "info", t("detail_share"), window.location.href, t("confirm"));
    }
  };

  /* ติดต่อผ่าน LINE — ยังไม่ได้ต่อลิงก์เปิดแอป LINE จริง จึงโชว์ไอดีไว้ให้คัดลอกไปก่อน */
  line = () => {
    const agent = this.state.data.agent || {};
    alert(
      "",
      "info",
      t("detail_contact_title"),
      `${t("detail_contact_line")} : ${agent.line}\n${t("detail_contact_soon")}`,
      t("confirm")
    );
  };

  /**
   * โทรเลย — ถามยืนยันก่อนโทรออกเสมอ
   * บนมือถือ tel: จะเด้งหน้าโทรออกทันทีที่กด ถ้าไม่ถามก่อนผู้ใช้ที่กดพลาด
   * (โดยเฉพาะแถบปุ่มล่างจอที่อยู่ในระยะนิ้วโป้ง) จะโทรหาผู้ดูแลประกาศโดยไม่ตั้งใจ
   * และหน้าจอนี้ยังบอกด้วยว่ากำลังจะโทรหาใคร เบอร์อะไร ก่อนจะตัดสินใจ
   */
  call = async () => {
    const agent = this.state.data.agent || {};

    // ผู้ดูแลประกาศยังไม่ได้กรอกเบอร์ไว้ — ไม่มีอะไรให้โทร บอกตรง ๆ ดีกว่าปล่อยให้กดแล้วเงียบ
    if (!agent.phone) {
      alert("", "info", t("detail_contact_title"), t("detail_call_no_phone"), t("confirm"));
      return;
    }

    const who = [agent.nickname, agent.company].filter(Boolean).join(" · ");
    const confirmed = await alert(
      "",
      "question",
      t("detail_call_confirm_title"),
      `${who}\n${agent.phone}\n\n${t("detail_call_confirm_desc")}`,
      t("detail_contact_call"),
      t("cancel")
    );
    if (!confirmed) return;

    // เครื่องโทรศัพท์รับได้เฉพาะตัวเลขกับ + (เบอร์ที่แอดมินกรอกอาจมีขีดหรือเว้นวรรค)
    window.location.href = `tel:${agent.phone.replace(/[^0-9+]/g, "")}`;
  };

  /* ------------------------------------------- ลงทะเบียนสนใจทรัพย์ (ฝากข้อมูล) */

  openLead = () => this.setState({ modal_lead: true });
  closeLead = () => this.setState({ modal_lead: false });

  /* ฟอร์มในกล่องเรียกใช้ตอนกรอกไม่ครบ — ใช้ป๊อปอัปชุดเดียวกับหน้านี้ทั้งหมด */
  warn = (message) => alert("", "warning", t("warning"), message, t("confirm"));

  /**
   * ส่งข้อมูลผู้สนใจเข้าระบบหลังบ้าน (ตาราง customers เดียวกับปุ่ม "เพิ่มลูกค้า")
   * คืน true เมื่อบันทึกสำเร็จ เพื่อให้กล่องฟอร์มล้างค่าที่กรอกไว้
   */
  saveLead = async (payload) => {
    loading(true);
    const result = await submitPropertyLead(this.props.router.params.id, payload);
    loading(false);

    if (!result.status) {
      await alert("", "warning", t("warning"), t("lead_fail_desc"), t("confirm"));
      return false;
    }

    this.setState({ modal_lead: false });
    await alert(
      "",
      "success",
      t("lead_success_title"),
      // รหัสคำขอไว้อ้างอิงตอนผู้ดูแลโทรกลับ — ไม่มีก็ไม่ต้องขึ้นบรรทัดเปล่า
      `${t("lead_success_desc")}${result.code ? `\n\n${t("lead_ref_code")} ${result.code}` : ""}`,
      t("confirm")
    );
    return true;
  };

  /* เส้นทางนำทางด้านบน — กดกลับไปหน้าแรกได้ ส่วนที่เหลือเป็นบริบทของทรัพย์ */
  breadcrumb = (data) => {
    const meta = typeMeta(data.type);
    const deal = isRent(data.deals) ? "home_nav_rent" : "home_nav_sale";

    return (
      <nav className="hm-pd-crumb" aria-label="breadcrumb">
        <div className="hm-container">
          <a href="/">{t("home_nav_home")}</a>
          <span className="hm-ico">{ICON.chevron_right_small}</span>
          <button type="button" onClick={() => this.soon(t(deal))}>{t(deal)}</button>
          <span className="hm-ico">{ICON.chevron_right_small}</span>
          <button type="button" onClick={() => this.soon(t(meta.label))}>{t(meta.label)}</button>
          <span className="hm-ico">{ICON.chevron_right_small}</span>
          <span className="is-current">{data.title || data.project}</span>
        </div>
      </nav>
    );
  };

  render() {
    const { data, similar, favorites, fav_pop, show_bar } = this.state;

    if (!data) return <div className="mn-home" />;

    const agent = data.agent || {};
    const saved = !!favorites[data.id];

    return (
      <div className="mn-home mn-home--detail">
        <HomeNavbar onPost={openFreePost} />

        {this.breadcrumb(data)}

        <div className="hm-container hm-pd-wrap">
          {/* ปุ่มบันทึกทรัพย์อยู่ที่หัวข้อประกาศ (PropertyHead) ที่เดียว */}
          <PropertyGallery
            photos={data.photos}
            status={data.status}
            deals={data.deals}
            badge={data.badge}
          />

          <div className="hm-pd-layout">
            <div className="hm-pd-main">
              <PropertyHead
                data={data}
                favorite={saved}
                onFavorite={() => this.toggleFavorite(data.id)}
                onShare={this.share}
              />

              <PropertyBody
                data={data}
                onFloorPlan={(file) => this.soon(file.name)}
                onVideo={() => this.soon(t("detail_video"))}
              />

              {/* สถิติของประกาศ — ช่วยให้ผู้ซื้อประเมินว่าทรัพย์นี้ใหม่หรือมีคนสนใจแค่ไหน */}
              <div className="hm-pd-stats">
                <span>
                  <span className="hm-ico">{ICON.eye}</span>
                  {t("detail_views")} {Number(data.views).toLocaleString()}
                </span>
                <span>
                  <span className="hm-ico">{ICON.heart}</span>
                  {t("detail_saves")} {data.saved}
                </span>
                <span>
                  <span className="hm-ico">{ICON.clock}</span>
                  {t("detail_posted")} {postedWord(data.posted_days)}
                </span>
                <span>
                  <span className="hm-ico">{ICON.tag}</span>
                  {t("detail_code")} {data.code}
                </span>
              </div>
            </div>

            <PropertySide
              data={data}
              agent={agent}
              onLine={this.line}
              onCall={this.call}
              onAppointment={this.openLead}
            />
          </div>
        </div>

        <PropertySimilar
          items={similar}
          favorites={favorites}
          fav_pop={fav_pop}
          onFavorite={this.toggleFavorite}
          onOpen={(item) => window.open(`/property/${item.id}`, "_blank", "noopener,noreferrer")}
          onViewAll={() => this.props.router.navigate("/")}
        />

        <HomeFooter
          data={this.state.content.contact.footer}
          socials={this.state.content.contact.socials}
          onLink={(key) => this.soon(t(key))}
          onSubscribe={(email) =>
            email
              ? alert("", "success", t("success"), t("home_footer_subscribe_done"), t("confirm"))
              : alert("", "warning", t("warning"), t("home_footer_email_required"), t("confirm"))
          }
        />

        {/* แถบติดต่อล่างจอ — เห็นเฉพาะมือถือ ให้ปุ่มติดต่ออยู่ในระยะนิ้วโป้งตลอด */}
        <div className={`hm-pd-bar ${show_bar ? "is-show" : ""}`}>
          <div className="hm-pd-bar__price">
            {isRent(data.deals) && !!data.rent && (
              <>
                {money(data.rent)} <small>{t("baht_per_month")}</small>
              </>
            )}
            {isSale(data.deals) && !isRent(data.deals) && !!data.price && (
              <>
                {money(data.price)} <small>{t("unit_baht")}</small>
              </>
            )}
          </div>
          {/* ปุ่มหลักของแถบคือ "ลงทะเบียนสนใจ" — โทร/LINE เหลือเป็นไอคอน เพราะสองช่องทางนั้น
              ต้องรอผู้ดูแลว่าง ส่วนการฝากข้อมูลทำได้ตลอดเวลาแม้นอกเวลาทำการ
              ปุ่ม LINE ขึ้นเฉพาะเมื่อผู้ดูแลกรอกไอดีไว้ ให้ตรงกับการ์ดผู้ติดต่อด้านบน */}
          <div className="hm-pd-bar__actions">
            <button type="button" className="hm-pd-btn hm-pd-btn--outline" onClick={this.call}>
              <span className="hm-ico">{ICON.phone}</span>
            </button>
            {!!agent.line && (
              <button type="button" className="hm-pd-btn hm-pd-btn--line" onClick={this.line}>
                <span className="hm-ico">{ICON.comment}</span>
              </button>
            )}
            <button type="button" className="hm-pd-btn hm-pd-btn--primary" onClick={this.openLead}>
              <span className="hm-ico">{ICON.calendar}</span>
              {t("detail_contact_lead")}
            </button>
          </div>
        </div>

        <PropertyLead
          open={this.state.modal_lead}
          data={data}
          agent={agent}
          onClose={this.closeLead}
          onWarn={this.warn}
          onSubmit={this.saveLead}
        />
      </div>
    );
  }
}

export default withRouter(PropertyDetail);
